import is from '@sindresorhus/is';
import type { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export const useJobItemVersions = ({
    architectureId,
    enabled,
    itemId,
    itemType,
}: {
    readonly architectureId: string;
    readonly enabled?: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
}): UseQueryResult<JobProfileVersions, Error> => {
    const { fetchJobLevelVersions, fetchJobRoleVersions } = useApplicationFetchers();

    const getQueryKey = (): QueryKey => {
        if (itemType === 'job-role') {
            return jobArchitectureKeys.roleVersions(architectureId, itemId);
        }
        if (itemType === 'job-level') {
            return jobArchitectureKeys.levelVersions(architectureId, itemId);
        }

        throw new Error(`Invalid item type: ${itemType}`);
    };

    return useQuery({
        enabled,
        placeholderData: (previousData) => {
            if (is.nullOrUndefined(previousData)) {
                return undefined;
            }

            return previousData.some((version) => version.itemId === itemId) ? previousData : undefined;
        },
        queryFn: async () => {
            const result =
                itemType === 'job-role'
                    ? await fetchJobRoleVersions({ jobArchitectureId: architectureId, jobRoleId: itemId })
                    : await fetchJobLevelVersions({ jobArchitectureId: architectureId, jobLevelId: itemId });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: getQueryKey(),
        staleTime: 60_000,
        throwOnError: false,
    });
};
